import { readCalendlyMeetingUrl } from "@fitness-app/client-shared/features/consultations/upcoming-consultation";
import { CalendarEventStatus } from "@fitness-app/data-models/entities/CalendarEvent";

import { type PillTone } from "~/components/EventCalendar/EventPopover";

/**
 * A `calendar_event` consultation as the trainer calendar reads it. Only the slices of the Calendly
 * payload the chip and the popover use come over the wire: a busy trainer has ~600 rows a month and
 * the full `eventData` is ~1.2 kB a row.
 */
export interface TrainerConsultation {
  id: string;
  traineeId: string | null;
  clientName: string | null;
  clientEmail: string | null;
  status: CalendarEventStatus;
  startTime: string;
  endTime: string;
  meetingUrl: string | null;
  /** `eventData.scheduled_event.name` - the Calendly event type, e.g. "Cotygodniowa konsultacja Hollywood - 10 min". */
  eventName: string | null;
  /** `eventData.scheduled_event.location`, raw: `{ type, location, join_url, ... }`. */
  location: unknown;
  /** `eventData.scheduled_event.cancellation`, present only once Calendly cancelled it. */
  cancellation: { canceled_by?: string | null; reason?: string | null } | null;
  /** Null when the booking e-mail matched no client, or RLS hides her from this viewer. */
  trainee: { id: string; firstName: string | null; lastName: string | null } | null;
}

export const TRAINER_CONSULTATION_COLUMNS = [
  "id",
  "traineeId",
  "clientName",
  "clientEmail",
  "status",
  "startTime",
  "endTime",
  "meetingUrl",
  "eventName:eventData->scheduled_event->>name",
  "location:eventData->scheduled_event->location",
  "cancellation:eventData->scheduled_event->cancellation",
  "trainee:traineeId(id, firstName, lastName)",
].join(", ");

export type ConsultationChannel = "outboundCall" | "inboundCall" | "zoom" | "link" | "other";

export interface ConsultationContact {
  channel: ConsultationChannel;
  /** `outbound_call`: the client's number the trainer dials; `inbound_call`: the number she dials. */
  phone: string | null;
  meetingUrl: string | null;
}

export const CHANNEL_LABELS: Record<ConsultationChannel, string> = {
  outboundCall: "Telefon - dzwoni trener",
  inboundCall: "Telefon - dzwoni klientka",
  zoom: "Zoom",
  link: "Link do spotkania",
  other: "Inna",
};

const readLocation = (location: unknown) =>
  typeof location === "object" && location !== null ? (location as Record<string, unknown>) : null;

const readText = (value: unknown) => (typeof value === "string" && value.trim() ? value.trim() : null);

/**
 * How the call happens, with the same precedence as the client's banner: a link the trainer set in
 * admin wins, then whatever link Calendly attached (Zoom, a URL in a custom location), then the phone.
 * The phone number stays even when a link wins - on an `outbound_call` it is still the client's number.
 */
export const getConsultationContact = (consultation: Pick<TrainerConsultation, "location" | "meetingUrl">) => {
  const location = readLocation(consultation.location);
  const type = location?.type;
  const phone = type === "outbound_call" || type === "inbound_call" ? readText(location?.location) : null;
  const trainerUrl = readText(consultation.meetingUrl);
  const calendlyUrl = readCalendlyMeetingUrl({ scheduled_event: { location: consultation.location } });
  const meetingUrl = trainerUrl ?? calendlyUrl;

  const channel: ConsultationChannel = trainerUrl
    ? "link"
    : calendlyUrl
      ? type === "zoom"
        ? "zoom"
        : "link"
      : type === "outbound_call"
        ? "outboundCall"
        : type === "inbound_call"
          ? "inboundCall"
          : "other";

  return { channel, phone, meetingUrl } satisfies ConsultationContact;
};

export const getClientName = (consultation: Pick<TrainerConsultation, "trainee" | "clientName" | "clientEmail">) => {
  const traineeName = [consultation.trainee?.firstName, consultation.trainee?.lastName].filter(Boolean).join(" ");
  return traineeName || readText(consultation.clientName) || consultation.clientEmail || "Klientka";
};

/** `tel:` wants the number without the spaces Calendly formats it with ("+48 798 337 747"). */
export const toTelHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, "")}`;

export const STATUS_ORDER: CalendarEventStatus[] = [
  CalendarEventStatus.ACTIVE,
  CalendarEventStatus.ATTENDED,
  CalendarEventStatus.NO_SHOW,
  CalendarEventStatus.CANCELED,
];

// The status colours of the client calendar's popover pill, except cancelled: grey, so it does not
// compete with the calls that will happen.
export const STATUS_PILL_TONES: Record<CalendarEventStatus, PillTone> = {
  [CalendarEventStatus.ACTIVE]: "blue",
  [CalendarEventStatus.ATTENDED]: "green",
  [CalendarEventStatus.NO_SHOW]: "amber",
  [CalendarEventStatus.CANCELED]: "gray",
};

export const STATUS_CHIP_TONES: Record<CalendarEventStatus, string> = {
  [CalendarEventStatus.ACTIVE]: "border-blue-100 bg-blue-50 text-blue-950 hover:border-blue-200 hover:bg-blue-100",
  [CalendarEventStatus.ATTENDED]:
    "border-green-100 bg-green-50 text-green-950 hover:border-green-200 hover:bg-green-100",
  [CalendarEventStatus.NO_SHOW]:
    "border-amber-200 bg-amber-50 text-amber-950 hover:border-amber-300 hover:bg-amber-100",
  [CalendarEventStatus.CANCELED]: "border-gray-200 bg-gray-50 text-gray-400 hover:bg-gray-100",
};

export const countByStatus = (consultations: Pick<TrainerConsultation, "status">[]) => {
  const counts = Object.fromEntries(STATUS_ORDER.map((status) => [status, 0])) as Record<CalendarEventStatus, number>;
  for (const { status } of consultations) {
    if (status in counts) {
      counts[status] += 1;
    }
  }
  return counts;
};
